import React from 'react';
import { defineMessages, useIntl } from 'react-intl';

import StatusInformationIcon from '@atlaskit/icon/core/status-information';
// eslint-disable-next-line @atlaskit/design-system/no-emotion-primitives -- to be migrated to @atlaskit/primitives/compiled – go/akcss
import Box from '@atlaskit/primitives/box';
import Inline from '@atlaskit/primitives/inline';
import { xcss } from '@atlaskit/primitives/xcss';
import Radio from '@atlaskit/radio/radio';
import IconButton from '@atlaskit/button/icon/button';
import Tooltip from '@atlaskit/tooltip/Tooltip';

import { useSpaceRoleMode } from '@confluence/space-utils/entry-points/useSpaceRoleMode.ts';

const i18n = defineMessages({
	archivedSpacesTooltip: {
		id: 'bulk-permissions.space.archived-spaces-tooltip',
		defaultMessage: 'Archived spaces are not included in bulk permissions actions.',
		description: 'Tooltip text for the space selection section for the bulk access page',
	},
	archivedSpacesTooltipBulkAccess: {
		id: 'bulk-permissions.space.archived-spaces-tooltip-bulk-access',
		defaultMessage: 'Archived spaces are not included in bulk access actions.',
		description: 'Tooltip text for the space selection section for the bulk access page',
	},
	archivedSpacesTooltipIconLabel: {
		id: 'bulk-permissions.space.archived-spaces-tooltip-icon-label',
		defaultMessage: 'Archived space information',
		description: 'Label for the archived spaces tooltip icon',
	},
	spaceSelectionHeader: {
		id: 'bulk-permissions.space.space-selection-header',
		defaultMessage: 'Choose which spaces',
		description: 'Header text of the space selection section',
	},
	spaceAccessSelectionHeader: {
		id: 'bulk-permissions.space.space-access-selection-header',
		defaultMessage: 'On which spaces<asterisk>*</asterisk>',
		description: 'Header text of the space selection section for the bulk access page',
	},
	spaceSelectionOne: {
		id: 'bulk-permissions.space.space-selection-one',
		defaultMessage: 'All',
		description: 'Option to select all Confluence spaces including personal spaces',
	},
	spaceSelectionOneDescription: {
		id: 'bulk-permissions.space.space-selection-one.description.non-final',
		defaultMessage: 'All spaces within your tenant',
		description:
			'Description for the option to select all Confluence spaces including personal spaces',
	},
	spaceSelectionTwo: {
		id: 'bulk-permissions.space.space-selection-two',
		defaultMessage: 'All (excluding personal)',
		description: 'Option to select all Confluence spaces except personal spaces',
	},
	spaceSelectionTwoDescription: {
		id: 'bulk-permissions.space.space-selection-two.description.non-final',
		defaultMessage: 'All spaces within your tenant, except for personal spaces.',
		description:
			'Description for the option to select all Confluence spaces excluding personal spaces',
	},
	spaceSelectionFour: {
		id: 'bulk-permissions.space.space-selection-four',
		defaultMessage: 'Only personal',
		description: 'Option to select personal Confluence spaces',
	},
	spaceSelectionFourDescription: {
		id: 'bulk-permissions.space.space-selection-four.description.non-final',
		defaultMessage: 'Only select personal spaces within this tenant',
		description: 'Description for the option to select personal spaces',
	},
});

export enum SpaceSelection {
	AllExcludingPersonal = 'all-excluding-personal',
	AllIncludingPersonal = 'all-including-personal',
	OnlyPersonal = 'only-personal',
}

const spaceSelectionHeaderStyle = xcss({
	font: 'font.body.large',
	fontWeight: 'font.weight.medium',
	display: 'flex',
	flexDirection: 'row',
	paddingTop: 'space.300',
	paddingBottom: 'space.100',
});

const spaceSelectionRadioButtonStyle = xcss({
	marginBottom: 'space.300',
});

const asteriskStyle = xcss({
	// eslint-disable-next-line @atlaskit/design-system/ensure-design-token-usage -- Requested suppression for existing token string
	color: 'color.text.accent.red',
});

type SpaceSelectionRadioButtonProps = {
	spaceSelection: SpaceSelection;
	onRadioChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
	isDisabled?: boolean;
};

export const SpaceSelectionRadioButtons = ({
	spaceSelection,
	onRadioChange,
	isDisabled = false,
}: SpaceSelectionRadioButtonProps): React.JSX.Element => {
	const { formatMessage } = useIntl();
	const { isRbacEnabled } = useSpaceRoleMode();

	const spaceSelectionHeader = () => {
		if (isRbacEnabled) {
			return i18n.spaceAccessSelectionHeader;
		}
		return i18n.spaceSelectionHeader;
	};

	return (
		<>
			<Inline xcss={spaceSelectionHeaderStyle} space="space.050" alignBlock="center">
				<Box xcss={spaceSelectionHeaderStyle}>
					{formatMessage(spaceSelectionHeader(), {
						asterisk: (chunks: React.ReactNode[]) => <Box xcss={asteriskStyle}>{chunks}</Box>,
					})}

					<Tooltip
						content={
							isRbacEnabled
								? formatMessage(i18n.archivedSpacesTooltipBulkAccess)
								: formatMessage(i18n.archivedSpacesTooltip)
						}
						position="right"
					>
						{(tooltipProps) => (
							<IconButton
								icon={(iconProps) => <StatusInformationIcon {...iconProps} size="small" />}
								appearance="subtle"
								label={formatMessage(i18n.archivedSpacesTooltipIconLabel)}
								spacing="compact"
								shape="circle"
								{...tooltipProps}
							/>
						)}
					</Tooltip>
				</Box>
			</Inline>
			<Box xcss={spaceSelectionRadioButtonStyle}>
				<Radio
					value={SpaceSelection.AllIncludingPersonal}
					onChange={onRadioChange}
					isChecked={spaceSelection === SpaceSelection.AllIncludingPersonal}
					isDisabled={isDisabled}
					name="space-selection"
					label={formatMessage(i18n.spaceSelectionOne)}
				/>
				<Radio
					value={SpaceSelection.AllExcludingPersonal}
					onChange={onRadioChange}
					isChecked={spaceSelection === SpaceSelection.AllExcludingPersonal}
					isDisabled={isDisabled}
					name="space-selection"
					label={formatMessage(i18n.spaceSelectionTwo)}
				/>
				<Radio
					value={SpaceSelection.OnlyPersonal}
					onChange={onRadioChange}
					isChecked={spaceSelection === SpaceSelection.OnlyPersonal}
					isDisabled={isDisabled}
					name="space-selection"
					label={formatMessage(i18n.spaceSelectionFour)}
				/>
			</Box>
		</>
	);
};
